import React from 'react';
import { useSelector } from 'react-redux';
import { capitalize, toUpper } from 'lodash';
import moment from 'moment/moment';
import { SIcon, STable, STooltip } from '@chargebee/sting-react';
import { DotIndicator } from '@bbkAdminComponents/indicators/dot';
import { BrightbackChartHex } from '@bbkAdminComponents/indicators/indicator-colors';
import { EngagedStatusPill } from '@bbkAdminComponents/play/engaged-status-pill';
import { ExportPlayDataV2 } from '@bbkAdminComponents/play/export-play-data';
import { FulfillmentStatusPill } from '@bbkAdminComponents/play/fulfillment-status-pill';
import { useShowPlayDemoData } from '@bbkAdminComponents/play/play-context';
import { formatCurrencyOrNumber } from '@bbkAdminComponents/predictive/customer-risk-level-widget';
import { TippyTooltip } from '@bbkAdminComponents/tooltips/tippy-tooltip';
import { selectAppId } from '@bbkAdminRedux/app/selectors';
import { selectFromResultTyped } from '@bbkAdminRedux/redux_utils';
import { useGetDemoSuffix } from '@bbkAdminRedux/rtkq/app-persist/app-persist.slice';
import { selectExperiencesAudiencesPlaysReportFilter } from '@bbkAdminRedux/rtkq/app-persist/reports-filter';
import { BBKRequestContext } from '@bbkAdminRedux/rtkq/bbkApi.slice';
import { useSelectDatePicker } from '@bbkAdminRedux/rtkq/datepicker-persist.slice';
import { useSelectAllOffers } from '@bbkAdminRedux/rtkq/offers-and-lossAversions.slice';
import {
  reportsSlice,
  ReportType,
  type GetReportV2Args,
  type LifecycleCustomerReport,
  type ReportFilter,
} from '@bbkAdminRedux/rtkq/reports.slice';
import { isDefined } from '@bbkAdminRedux/rtkq/rtkq-utils';
import { FeatureFlagsE, useFeatureFlags } from '@bbkAdminUtils/feature-flags';
import { humanizeString } from '@bbkAdminUtils/utils';

type Props = {
  playId: string;
  offerId: string | null | undefined;
  totalCustomersCount: number;
  offerEntityIds: string[] | undefined;
};

export const CustomerSnapshotTableV2 = ({
  playId,
  offerId,
  totalCustomersCount,
  offerEntityIds,
}: Props) => {
  const appKey = useSelector(selectAppId);

  const isDemoData = useShowPlayDemoData();
  const showExtraDataFields = useFeatureFlags(
    FeatureFlagsE.ENABLE_PLAY_REPORTING_EXTRA_FIELDS
  );
  const offers = useSelectAllOffers({
    appKey,
    context: isDemoData ? BBKRequestContext.lifecycle_reports : undefined,
  });
  const offerSelected = offerId !== null && offerId !== undefined;

  const { startDate: start, endDate: end } = useSelectDatePicker();
  const suffix = useGetDemoSuffix();
  const reportFilters = useSelector(
    selectExperiencesAudiencesPlaysReportFilter(suffix)
  );

  const detailFilters: ReportFilter[] = [
    ...reportFilters,
    { field: 'play_id', operator: 'equals', value: playId },
    ...(offerSelected
      ? ([
          {
            field: 'offer_id',
            operator: 'equals',
            value: offerId,
          },
        ] as ReportFilter[])
      : []),
    //TODO: see if we can deprecate this field soon
    {
      field: 'target_type',
      operator: 'equals',
      value: 'OFFER',
    },
  ];

  const detailRequest = {
    report_type: ReportType.lifecycle_entity_metrics_customer_snapshot,
    parameters: {
      group_by_period: 'all',
    },
    filters: [detailFilters],
    limit: 5,
  };

  const detailArgs: GetReportV2Args = {
    appKey,
    start,
    end,
    report_type_requests: [detailRequest],
    context: isDemoData ? BBKRequestContext.lifecycle_reports : undefined,
  };

  const detailOpts = {
    skip: !appKey || !start || !end,
    selectFromResult: selectFromResultTyped<LifecycleCustomerReport>,
  };

  const {
    data: customerSnapshot,
    isFetching: isSnapshotFetching,
    isLoading: isSnapshotLoading,
  } = reportsSlice.endpoints.getReportV2.useQuery(detailArgs, detailOpts);
  const snapshotData = customerSnapshot?.customers;
  const hasData = snapshotData && snapshotData.length > 0;

  const emptyRevenueTooltip = (
    <STooltip
      label="No revenue to display yet — there haven't been any fulfilled items for this offer."
      width="regular"
      placement="top"
    >
      <span className="tw-underline tw-decoration-dotted tw-underline-offset-2">
        --
      </span>
    </STooltip>
  );

  return (
    <div>
      <div className="tw-mb-4 tw-mt-6 tw-flex tw-items-center tw-gap-2">
        <div className="tw-flex tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-primary-100 tw-bg-primary-50 tw-p-2">
          <SIcon name="user-check" size={18} className="tw-text-primary-600" />
        </div>
        <span className="tw-text-lg tw-font-semibold tw-text-neutral-900">
          Engaged Subscriber Preview ({snapshotData?.length ?? 0} of{' '}
          {formatCurrencyOrNumber(totalCustomersCount)})
        </span>
      </div>
      <div className="tw-mb-4 tw-flex tw-flex-row tw-items-center tw-justify-between">
        <div className="tw-text-sm tw-font-light tw-text-neutral-600">
          {offerSelected
            ? 'To view the full list of customers and users who have engaged with the offer, export the CSV.'
            : 'To view the full list of customers and users who have engaged with offers under this play, export the CSV.'}
        </div>
        {hasData && (
          <ExportPlayDataV2
            playId={playId}
            offerId={offerId}
            disabled={!hasData}
          />
        )}
      </div>
      {hasData || isSnapshotFetching || isSnapshotLoading ? (
        <div className="tw-mb-6 tw-w-full">
          <STable
            loading={isSnapshotFetching || isSnapshotLoading}
            loadingRows={5}
            loadingColumns={showExtraDataFields ? 8 : 7}
          >
            <STable.Head>
              <STable.Row>
                <STable.HeaderCell className="!tw-text-xs">
                  Customer
                </STable.HeaderCell>
                <STable.HeaderCell className="!tw-text-xs">
                  Offer Name
                </STable.HeaderCell>
                <STable.HeaderCell className="!tw-text-xs">
                  Customer ID
                </STable.HeaderCell>
                <STable.HeaderCell className="!tw-text-xs">
                  Last engaged date
                </STable.HeaderCell>
                <STable.HeaderCell className="!tw-text-xs">
                  Last engaged status
                </STable.HeaderCell>
                <STable.HeaderCell className="!tw-text-xs">
                  Fulfillment status
                </STable.HeaderCell>
                {showExtraDataFields && (
                  <STable.HeaderCell className="!tw-text-xs">
                    Revenue generated
                  </STable.HeaderCell>
                )}
                <STable.HeaderCell className="!tw-text-xs">
                  Last engaged user
                </STable.HeaderCell>
              </STable.Row>
            </STable.Head>
            <STable.Body>
              {snapshotData &&
                snapshotData.map((customer) => {
                  const offerIndex = customer.offer_id
                    ? offerEntityIds
                      ? offerEntityIds.findIndex(
                          (id) => id === customer.offer_id
                        )
                      : (offers?.findIndex((o) => o.id === customer.offer_id) ??
                        -1)
                    : -1;
                  return (
                    <STable.Row key={customer.customer_id}>
                      <STable.Cell>{customer.customer_name}</STable.Cell>
                      <STable.Cell>
                        <span className="tw-flex tw-items-center tw-gap-2">
                          {offerIndex >= 0 && (
                            <DotIndicator
                              className="tw-shrink-0"
                              dotColor={
                                BrightbackChartHex[
                                  offerIndex % BrightbackChartHex.length
                                ]!.key
                              }
                            />
                          )}
                          {customer.offer_name ??
                            offers?.find((o) => o.id === customer.offer_id)
                              ?.display_name ??
                            '--'}
                        </span>
                      </STable.Cell>
                      <STable.Cell>{customer.customer_id}</STable.Cell>
                      <STable.Cell>
                        {moment(customer.last_engaged_date).format(
                          'MM/DD/YYYY'
                        )}
                      </STable.Cell>
                      <STable.Cell>
                        <span className="tw-flex tw-items-center">
                          <EngagedStatusPill
                            status={toUpper(customer.last_engaged_status)}
                          />
                          {capitalize(customer.last_engaged_status)}
                        </span>
                      </STable.Cell>
                      <>
                        <STable.Cell>
                          <span className="tw-flex tw-items-center">
                            {customer.fulfillment_status ? (
                              <>
                                <FulfillmentStatusPill
                                  status={customer.fulfillment_status}
                                />
                                {humanizeString(customer.fulfillment_status)}
                              </>
                            ) : (
                              <TippyTooltip
                                content={
                                  <span className="tw-font-normal">
                                    Fulfillment doesn&apos;t start until the
                                    offer has been accepted
                                  </span>
                                }
                              >
                                <span className="tw-underline tw-decoration-dotted tw-underline-offset-2">
                                  N/A
                                </span>
                              </TippyTooltip>
                            )}
                          </span>
                        </STable.Cell>
                        {showExtraDataFields && (
                          <STable.Cell>
                            {isDefined(customer.revenue)
                              ? formatCurrencyOrNumber(customer.revenue, true)
                              : emptyRevenueTooltip}
                          </STable.Cell>
                        )}
                      </>
                      <STable.Cell>{customer.user_id}</STable.Cell>
                    </STable.Row>
                  );
                })}
            </STable.Body>
          </STable>
        </div>
      ) : (
        <div className="tw-flex tw-w-full tw-items-center tw-justify-center tw-pb-8 tw-pt-4">
          <span className="tw-text-neutral-600">No snapshot data found</span>
        </div>
      )}
    </div>
  );
};
